// src/components/HeaderSubscriptionStatus.js
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { SERVER_BASE_URL } from '../config';

const HeaderSubscriptionStatus = () => {
  const { user } = useAuth();
  const [showTooltip, setShowTooltip] = useState(false);
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);

  useEffect(() => {
    const fetchSubscriptionStatus = async () => {
      const token = localStorage.getItem('token');
      if (!token) return;
      try {
        const res = await fetch(`${SERVER_BASE_URL}/api/subscription/status`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = await res.json();
        setSubscriptionStatus(data);
      } catch (error) {
        console.error('Error fetching subscription status:', error);
      }
    };

    fetchSubscriptionStatus();
  }, []);

  if (!user || !subscriptionStatus) return null;
  if (user.role === 'admin') return null;

  const isActive =
    subscriptionStatus.is_subscription_active || subscriptionStatus.is_trial_active;

  const getStatusInfo = () => {
    if (subscriptionStatus.is_subscription_active) {
      return {
        color: 'bg-green-500',
        text: subscriptionStatus.subscription_type || 'Active',
        textColor: 'text-green-100',
      };
    } else if (subscriptionStatus.is_trial_active && subscriptionStatus.trial_ends_at) {
      const daysLeft = Math.ceil(
        (new Date(subscriptionStatus.trial_ends_at) - new Date()) / (1000 * 60 * 60 * 24)
      );
      return {
        color: 'bg-blue-500',
        text: `Trial (${daysLeft}d)`,
        textColor: 'text-blue-100',
      };
    } else {
      return {
        color: 'bg-red-500',
        text: 'Inactive',
        textColor: 'text-red-100',
      };
    }
  };

  const statusInfo = getStatusInfo();

  const getTooltipContent = () => {
    const endDate =
      subscriptionStatus.subscription_end || subscriptionStatus.trial_ends_at;

    return (
      <div className="bg-gray-900 text-white text-xs rounded py-2 px-3 shadow-lg border border-gray-700 min-w-48">
        <div className="font-medium mb-1">Your Subscription</div>
        <div className="flex justify-between items-center mb-1">
          <span>Status:</span>
          <span
            className={`px-2 py-1 rounded text-xs ${
              isActive ? 'bg-green-600 text-green-100' : 'bg-red-600 text-red-100'
            }`}
          >
            {statusInfo.text}
          </span>
        </div>
        {endDate && (
          <div className="flex justify-between items-center">
            <span>Until:</span>
            <span>{new Date(endDate).toLocaleDateString()}</span>
          </div>
        )}
        {!isActive && (
          <div className="mt-2 pt-2 border-t border-gray-700">
            <Link
              to="/subscription"
              className="text-blue-400 hover:text-blue-300 text-xs"
            >
              Activate subscription →
            </Link>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="relative flex items-center">
      <div
        className="relative cursor-pointer"
        onMouseEnter={() => setShowTooltip(true)}
        onMouseLeave={() => setShowTooltip(false)}
        onClick={() => setShowTooltip(!showTooltip)}
      >
        <div className="flex items-center px-3 py-2 rounded transition duration-200 hover:bg-gray-700">
          <div
            className={`w-2.5 h-2.5 rounded-full mr-2 ${statusInfo.color}`}
          ></div>
          <span className="text-xs text-white font-medium">{statusInfo.text}</span>
        </div>

        {showTooltip && (
          <div className="absolute top-full right-0 mt-2 z-50">
            {getTooltipContent()}
          </div>
        )}
      </div>
    </div>
  );
};

export default HeaderSubscriptionStatus;